<template>
  
  <SubHeading :home="home" :tabs="visibleTabs" :shorts="true" :currTab="currTab" color="cyan darken-3" @active="handleActive" />

  <!-- breadcrumbs   -->
  <nav class="sba-collection-breadcrumb z-depth-0">
    <div class="container nav-wrapper">
      <div class="sba-collection-breadcrumb-row">
        <router-link to="/" class="breadcrumb sba-collection-crumb teal-text">
          <i class="material-icons">home</i>
          <span>Dashboard</span>
        </router-link>
        <i class="material-icons sba-collection-crumb-separator">chevron_right</i>
        <span v-if="currTab?.title?.toLowerCase()?.includes('home')" class="breadcrumb sba-collection-crumb blue-grey-text">
          <span>Finance Home</span>
        </span>
        <a v-else href="!#" class="breadcrumb sba-collection-crumb blue-grey-text"
          @click.prevent="handleActive(tabs.find(x => x.title.includes('Home')))">
          <span>Finance Home</span>
        </a>
        <template v-if="!currTab?.title?.toLowerCase()?.includes('home')">
          <i class="material-icons sba-collection-crumb-separator">chevron_right</i>
          <span class="breadcrumb sba-collection-crumb blue-grey-text">
            <span>{{ currTab?.title?.split(" ")[0] }}</span>
          </span>
        </template>
        <span class="sba-collection-context">{{ breadcrumbContext }}</span>
      </div>
    </div>
  </nav>

  <!-- search layer -->
  <!-- <div class="grey lighten-4">
    <div class="container section"></div>
  </div> -->

  <!-- Top -->
  <div id="top"></div>

  <div class="container finance-page-shell">
    <div class="finance-workbench">
      <LoadingDiv v-if="!isReady" />

      <template v-else>

        <div class="finance-view-shell sba-slide-in" :key="currTab?.id">
          <CollectionModuleLanding
            v-if="currTab?.title.toLowerCase().includes('home')"
            moduleName="Finance"
            heading="Finance Operations"
            description="Control claims payment, invoice approval, and account updates from one focused desk."
            scopeLabel="Payment Control"
            accent="green"
            :tabs="tabs"
            :cardCopy="landingCards"
            @active="handleActive"
          />

            <!-- Invoices -->
            <InvoicesDiv
              :resetKey="invoicesResetKey"
              v-if="currTab?.title.toLowerCase().includes('invoices')"
            />

            <!-- Claims -->
            <ClaimsDiv
              :resetKey="claimsResetKey"
              v-if="currTab?.title.toLowerCase().includes('claims')"
            />

            <!-- Account Updates -->
            <AccountUpdateDiv
              :resetKey="accountsResetKey"
              v-if="currTab?.title.toLowerCase().includes('accounts')"
            />
        </div>
        <!-- </div> -->
      </template>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import useModuleNavigation from "@/composables/useModuleNavigation";
import SubHeading from "@/components/HeaderSubDiv.vue";
import LoadingDiv from "@/components/LoadingDiv.vue";
import InvoicesDiv from "@/components/finance/FinanceInvoicesComponent.vue";
import ClaimsDiv from "@/components/finance/FinanceClaimsComponent.vue";
import AccountUpdateDiv from "@/components/finance/FinanceAccountsUpdateComponent.vue";
import CollectionModuleLanding from "@/components/collection/CollectionModuleLandingComponent.vue";


export default {
  name: "financeHome",
  components: {
    SubHeading,
    LoadingDiv,
    InvoicesDiv,
    ClaimsDiv,
    AccountUpdateDiv,
    CollectionModuleLanding,
  },
  setup() {
    const route = useRoute();
    const home = ref(null);
    const isReady = ref(true);
    const error = ref(null);
    const currTab = ref(null);
    const claimsResetKey = ref(0);
    const invoicesResetKey = ref(0);
    const accountsResetKey = ref(0);
    const tabs = ref([]);
    const { activateLocalTab } = useModuleNavigation();
    const visibleTabs = computed(() => tabs.value.filter((tab) => !tab.title?.includes("Home")));
    const breadcrumbContext = computed(() => {
      if (currTab.value?.title?.toLowerCase()?.includes("home")) {
        return `${currTab.value?.name || "Finance"} Workspace`;
      }

      return `${currTab.value?.title || "Finance"} Workspace`;
    });
    const landingCards = {
      "Claims Payment": {
        icon: "payments",
        description: "Process approved claim batches and prepare payment actions.",
      },
      "Invoices Approval": {
        icon: "receipt_long",
        description: "Review premium invoices and confirm payment status.",
      },
      "Accounts": {
        icon: "account_balance",
        description: "Maintain finance-facing account records and payment references.",
      },
    };
    const routeSections = {
      "claims-payment": "Claims Payment",
      "invoices-approval": "Invoices Approval",
      accounts: "Accounts",
    };

    // start
    onMounted(() => {
      // page name
      document.title = "Finance";
    });

    // Initiate
    onMounted(() => {
      // load tab
      tabs.value.push({ id: 1, name: "Finance", title: "Home", icon: null, url: null, hash: null, });
      tabs.value.push({ id: 2, name: "Finance", title: "Claims Payment", icon: null, url: null, hash: null, });
      tabs.value.push({ id: 3, name: "Finance", title: "Invoices Approval", icon: null, url: null, hash: null, });
      tabs.value.push({ id: 4, name: "Finance", title: "Accounts", icon: null, url: null, hash: null, });
      
      // set default
      currTab.value = tabs.value.find(x => x.title.includes("Home"));
      // set home
      home.value = tabs.value.find(x => x.title.includes("Home"));

      activateRouteSection();
    });

    // handle curr status
    const handleActive = (tab) => {
      if (tab?.title?.toLowerCase()?.includes("claims")) {
        claimsResetKey.value++;
      }

      if (tab?.title?.toLowerCase()?.includes("invoices")) {
        invoicesResetKey.value++;
      }

      if (tab?.title?.toLowerCase()?.includes("accounts")) {
        accountsResetKey.value++;
      }

      activateLocalTab(tab, currTab);
    };

    function activateRouteSection() {
      const targetTitle = routeSections[route.query.section];
      const targetTab = tabs.value.find(x => x.title === targetTitle);

      if (targetTab && currTab.value?.id !== targetTab.id) {
        handleActive(targetTab);
      }
    }

    watch(
      () => route.query.section,
      () => activateRouteSection()
    );


    return {
      home,
      handleActive,
      isReady,
      error,
      currTab,
      claimsResetKey,
      invoicesResetKey,
      accountsResetKey,
      tabs,
      visibleTabs,
      breadcrumbContext,
      landingCards,
    };
  },
};
</script>

<style scoped>
.finance-page-shell {
  margin-top: -14px;
  padding-bottom: 44px;
  padding-top: 0;
  position: relative;
  z-index: 1;
}

.finance-workbench {
  color: #263238;
}

.finance-view-shell :deep(.collection-landing-head),
.finance-view-shell :deep(.workflow-card),
.finance-view-shell :deep(.premium-queue-head),
.finance-view-shell :deep(.payment-batch-head),
.finance-view-shell :deep(.claim-batch-row),
.finance-view-shell :deep(.payment-empty-state),
.finance-view-shell :deep(.finance-batch-detail),
.finance-view-shell :deep(.batch-detail-head),
.finance-view-shell :deep(.batch-table-wrap),
.finance-view-shell :deep(.batch-action-strip),
.finance-view-shell :deep(.invoice-review-head),
.finance-view-shell :deep(.invoice-list-card),
.finance-view-shell :deep(.invoice-row-card),
.finance-view-shell :deep(.finance-approval-card),
.finance-view-shell :deep(.accounts-review-head),
.finance-view-shell :deep(.accounts-list-card),
.finance-view-shell :deep(.account-row-card),
.finance-view-shell :deep(.accounts-empty-state) {
  animation: sbaSlideIn var(--sba-slide-duration, 260ms) ease-out both;
}

@media (prefers-reduced-motion: reduce) {
  .finance-view-shell :deep(.collection-landing-head),
  .finance-view-shell :deep(.workflow-card),
  .finance-view-shell :deep(.premium-queue-head),
  .finance-view-shell :deep(.payment-batch-head),
  .finance-view-shell :deep(.claim-batch-row),
  .finance-view-shell :deep(.payment-empty-state),
  .finance-view-shell :deep(.finance-batch-detail),
  .finance-view-shell :deep(.batch-detail-head),
  .finance-view-shell :deep(.batch-table-wrap),
  .finance-view-shell :deep(.batch-action-strip),
  .finance-view-shell :deep(.invoice-review-head),
  .finance-view-shell :deep(.invoice-list-card),
  .finance-view-shell :deep(.invoice-row-card),
  .finance-view-shell :deep(.finance-approval-card),
  .finance-view-shell :deep(.accounts-review-head),
  .finance-view-shell :deep(.accounts-list-card),
  .finance-view-shell :deep(.account-row-card),
  .finance-view-shell :deep(.accounts-empty-state) {
    animation: none;
  }
}

@media only screen and (max-width: 900px) {
  .finance-page-shell {
    width: 92%;
  }
}

@media only screen and (max-width: 600px) {
  .finance-page-shell {
    margin-top: -10px;
    padding-bottom: 34px;
    width: 94%;
  }

  .finance-view-shell :deep(.premium-queue-head),
  .finance-view-shell :deep(.payment-batch-head),
  .finance-view-shell :deep(.claim-batch-row),
  .finance-view-shell :deep(.payment-empty-state),
  .finance-view-shell :deep(.finance-batch-detail),
  .finance-view-shell :deep(.batch-detail-head),
  .finance-view-shell :deep(.invoice-review-head),
  .finance-view-shell :deep(.invoice-list-card),
  .finance-view-shell :deep(.invoice-row-card),
  .finance-view-shell :deep(.finance-approval-card),
  .finance-view-shell :deep(.accounts-review-head),
  .finance-view-shell :deep(.accounts-list-card),
  .finance-view-shell :deep(.account-row-card),
  .finance-view-shell :deep(.accounts-empty-state) {
    border-radius: 6px;
  }
}
</style>
